import { AxiosResponse } from 'axios';
import { Country } from '@/types';
import { LogExportResponse } from '@/log';
import { EndUser } from '@/enduser';
import { Page } from '@/common';
import { client } from '@/client';

export enum SafeMailStatus {
  NEW = 'New',
  PROCESSING = 'Processing',
  DONE = 'Done',
}

export type SafeMailInboxMessage = {
  id: string;
  ssn: string;
  country: Country | null;
  userDisplayName: string;
  status: {
    value: SafeMailStatus;
    changedBy?: string;
    changedAt?: number; // UNIX timestamp (seconds)
  };
  datetime: number; // UNIX timestamp (seconds)
  formId?: number;
  bankIdToken?: string;
  responded: boolean;
  responseTo?: string;
  threadId?: string;
  threadSize: number;
  formName?: string;
  subject?: string;
  content?: string;
  input?: Record<string, string>;
  emailProperty?: string;
  attachments: {
    name: string;
  }[];
};

export type SentMessage = {
  id: string;
  creationDatetime: number; // UNIX timestamp (seconds)
  ssn: string;
  country: Country | null;
  subject: string;
  body: string;
  displayName: string;
  email?: string;
  phone?: string;
  responseTo?: string;
  threadId?: string;
  threadSize: number;
  requestSignature: boolean;
  messageSigned: boolean;
  messageRead: boolean;
  signature?: string;
  attachments: {
    name: string;
  }[];
  recipients?: {
    signatureDatetime?: number;
    ssn: string;
    displayName: string;
    email?: string;
    phone?: string;
    country: Country;
  }[];
};

export type SendMessageRequest = {
  ssn: string;
  country: Country;
  subject: string;
  body: string;
  displayName: string;
  email?: string;
  phone?: string;
  autodeleteDate: string; // yyyy-MM-dd
  requestSignature: boolean;
  canReply: boolean;
  replyTo?: string;
  attachments: File[];
};

export type MessageRecipient = {
  ssn: string;
  email?: string;
  phone?: string;
  country: Country;
};

export type SafeMailThreadMessage = {
  id: string;
  direction: 'SENT' | 'RECEIVED';
  datetime: number; // UNIX timestamp (seconds)
  body: string;
  attachments: {
    name: string;
  }[];
};

export type SafeMailThread = {
  subject: string;
  messages: SafeMailThreadMessage[];
};

export async function getSafeMailThread(
  threadId: string,
  signal?: AbortSignal,
): Promise<SafeMailThread> {
  return client
    .get<SafeMailThread>(`/message/thread/${threadId}`, { signal })
    .then((response) => response.data);
}

export async function getSafeMailPopulateData(
  ssn: string,
  country: Country,
  signal?: AbortSignal,
): Promise<EndUser | null> {
  return client
    .get<EndUser | null>(`/message/enduser`, {
      params: {
        ssn,
        country,
      },
      signal,
    })
    .then((response) => response.data);
}

export async function getSafeMailInboxMessageAttachment(
  messageId: string,
  attachmentName: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return client
    .get<Blob>(`/attachment/inbox/${messageId}/attachment/${attachmentName}`, {
      responseType: 'blob',
      signal,
    })
    .then((response) => response.data);
}

export async function getSafeMailSentMessageSignature(
  messageId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return client
    .get<Blob>(`/attachment/sent/${messageId}/signature`, {
      responseType: 'blob',
      signal,
    })
    .then((response) => response.data);
}

export async function getSafeMailSentMessageAttachment(
  messageId: string,
  attachmentName: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return client
    .get<Blob>(`/attachment/sent/${messageId}/attachment/${attachmentName}`, {
      responseType: 'blob',
      signal,
    })
    .then((response) => response.data);
}

export async function sendMessage(
  request: SendMessageRequest,
  signal?: AbortSignal,
): Promise<SentMessage> {
  const data = new FormData();

  for (const [key, value] of Object.entries(request)) {
    if (key === 'attachments') {
      continue;
    }

    if (value !== undefined && value !== null) {
      data.append(key, String(value));
    }
  }

  for (const attachment of request.attachments) {
    data.append(attachment.name, attachment);
  }

  return client
    .post<FormData, AxiosResponse<SentMessage>>('/message', data, { signal })
    .then((response) => response.data);
}

export async function addRecipients(
  messageId: string,
  recipients: MessageRecipient[],
  signal?: AbortSignal,
): Promise<string> {
  return client
    .post<{ recipients: MessageRecipient[] }, AxiosResponse<string>>(
      `/message/${messageId}/recipients`,
      {
        recipients,
      },
      { signal },
    )
    .then((response) => response.data);
}

export async function getSafeMailOutboxMessages(
  params?: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: 'id' | 'createdAt';
    sortDirection?: 'ascending' | 'descending';
    q?: string;
  },
  signal?: AbortSignal,
): Promise<Page<SentMessage>> {
  return client
    .get<Page<SentMessage>>('/message', { params, signal })
    .then((response) => response.data);
}

export async function getSafeMailInboxMessages(
  params?: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: 'id' | 'datetime';
    sortDirection?: 'ascending' | 'descending';
    form?: number;
    status?: string[];
    q?: string;
  },
  signal?: AbortSignal,
): Promise<Page<SafeMailInboxMessage>> {
  return client
    .get<Page<SafeMailInboxMessage>>('/message/inbox', {
      params: {
        ...params,
        status: params?.status?.join(','),
      },
      signal,
    })
    .then((response) => response.data);
}

export async function changeSafeMailInboxMessageStatus(
  messageId: string,
  status: SafeMailStatus,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .post<void>(`/message/inbox/${messageId}/status/${status}`, null, {
      signal,
    })
    .then((response) => response.data);
}

export async function deleteSafeMailInboxMessages(
  messageIds: string[],
  signal?: AbortSignal,
): Promise<number> {
  return client
    .post(
      '/message/inbox/delete',
      {
        ids: messageIds,
      },
      { signal },
    )
    .then((response) => response.data);
}

export async function deleteSafeMailSentMessages(
  messageIds: string[],
  signal?: AbortSignal,
): Promise<number> {
  return client
    .post(
      '/message/delete',
      {
        ids: messageIds,
      },
      { signal },
    )
    .then((response) => response.data);
}

export async function getSafeMailInboxPdf(
  messageId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return client
    .get<Blob>(`/message/inbox/${messageId}/pdf`, {
      responseType: 'blob',
      signal,
    })
    .then((response) => response.data);
}

export async function startSafeMailInboxPdfExport(
  params?: {
    form?: number;
    status?: string[];
  },
  signal?: AbortSignal,
): Promise<LogExportResponse> {
  return client
    .post<LogExportResponse>('/safemail/inbox/pdf-export', null, {
      params: {
        ...params,
        status: params?.status?.join(','),
      },
      signal,
    })
    .then((response) => response.data);
}

export async function getSafeMailInboxPdfExport(
  id: number,
  signal?: AbortSignal,
): Promise<LogExportResponse> {
  return client
    .get<LogExportResponse>(`/safemail/inbox/pdf-export/${id}`, { signal })
    .then((response) => response.data);
}

export async function getSafeMailInboxPdfExportResult(
  id: number,
  signal?: AbortSignal,
): Promise<Blob> {
  return client
    .get<Blob>(`/safemail/inbox/pdf-export/${id}/result`, {
      responseType: 'blob',
      signal,
    })
    .then((response) => response.data);
}

export async function startSafeMailInboxExcelExport(
  params?: {
    form?: number;
    status?: string[];
  },
  signal?: AbortSignal,
): Promise<LogExportResponse> {
  return client
    .post<LogExportResponse>('/safemail/inbox/excel-export', null, {
      params: {
        ...params,
        status: params?.status?.join(','),
      },
      signal,
    })
    .then((response) => response.data);
}

export async function getSafeMailInboxExcelExport(
  id: number,
  signal?: AbortSignal,
): Promise<LogExportResponse> {
  return client
    .get<LogExportResponse>(`/safemail/inbox/excel-export/${id}`, { signal })
    .then((response) => response.data);
}

export async function getSafeMailInboxExcelExportResult(
  id: number,
  signal?: AbortSignal,
): Promise<Blob> {
  return client
    .get<Blob>(`/safemail/inbox/excel-export/${id}/result`, {
      responseType: 'blob',
      signal,
    })
    .then((response) => response.data);
}

export async function startSafeMailOutboxExcelExport(
  signal?: AbortSignal,
): Promise<LogExportResponse> {
  return client
    .post<LogExportResponse>('/safemail/outbox/excel-export', null, { signal })
    .then((response) => response.data);
}

export async function getSafeMailOutboxExcelExport(
  id: number,
  signal?: AbortSignal,
): Promise<LogExportResponse> {
  return client
    .get<LogExportResponse>(`/safemail/outbox/excel-export/${id}`, { signal })
    .then((response) => response.data);
}

export async function getSafeMailOutboxExcelExportResult(
  id: number,
  signal?: AbortSignal,
): Promise<Blob> {
  return client
    .get<Blob>(`/safemail/outbox/excel-export/${id}/result`, {
      responseType: 'blob',
      signal,
    })
    .then((response) => response.data);
}
